import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useMutation } from '@tanstack/react-query';
import { LockClosedIcon } from '@heroicons/react/24/outline';
import { passwordResetService } from '../services/passwordResetService';
import type { ResetPasswordRequest } from '../services/passwordResetService';
import { Card, CardContent } from '../components/common/Card';
import { Input } from '../components/common/Input';
import { Button } from '../components/common/Button';
import { Alert } from '../components/common/Alert';
import { useTranslation } from 'react-i18next';

interface ResetPasswordForm {
  newPassword: string;
  confirmPassword: string;
}

export const ResetPassword = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [invalidToken, setInvalidToken] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<ResetPasswordForm>();

  const resetPasswordMutation = useMutation({
    mutationFn: (data: ResetPasswordForm) => 
      passwordResetService.resetPassword({
        token: token!,
        password: data.newPassword,
      }),
    onSuccess: () => {
      navigate('/password-reset-success');
    },
    onError: (error: any) => {
      if (error.response?.status === 400 && error.response?.data?.error?.includes('token')) {
        setInvalidToken(true);
      }
    },
  });

  useEffect(() => {
    if (!token) {
      setInvalidToken(true);
    }
  }, [token]);

  const onSubmit = (data: ResetPasswordForm) => {
    resetPasswordMutation.mutate(data);
  };

  const newPassword = watch('newPassword');

  if (invalidToken) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <Card>
            <CardContent className="p-6">
              <Alert variant="error">
                {t('auth.resetPassword.invalidLink')}
              </Alert>
              <div className="mt-4 text-center">
                <Link
                  to="/forgot-password"
                  className="font-medium text-primary-600 hover:text-primary-500"
                >
                  {t('auth.resetPassword.requestNewLink')}
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            {t('auth.resetPassword.title')}
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            {t('auth.resetPassword.description')}
          </p>
        </div>

        <Card>
          <CardContent className="p-6">
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              <Input
                label={t('auth.resetPassword.newPassword')}
                type="password"
                autoComplete="new-password"
                required
                {...register('newPassword', {
                  required: t('auth.resetPassword.passwordRequired'),
                  minLength: {
                    value: 6,
                    message: t('auth.resetPassword.passwordTooShort'),
                  },
                  pattern: {
                    value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
                    message: t('auth.resetPassword.passwordPattern'),
                  },
                })}
                error={errors.newPassword?.message}
              />

              <Input
                label={t('auth.resetPassword.confirmPassword')}
                type="password"
                autoComplete="new-password"
                required
                {...register('confirmPassword', {
                  required: t('auth.resetPassword.confirmRequired'),
                  validate: (value) =>
                    value === newPassword || t('auth.resetPassword.passwordsDontMatch'),
                })}
                error={errors.confirmPassword?.message}
              />

              {resetPasswordMutation.isError && (
                <Alert variant="error">
                  {(resetPasswordMutation.error as any)?.response?.data?.error || 
                   t('auth.resetPassword.error')}
                </Alert>
              )}

              <div>
                <Button
                  type="submit"
                  className="w-full"
                  isLoading={resetPasswordMutation.isPending}
                  disabled={resetPasswordMutation.isPending}
                >
                  {t('auth.resetPassword.resetButton')}
                </Button>
              </div>

              <div className="text-center">
                <Link
                  to="/login"
                  className="font-medium text-primary-600 hover:text-primary-500"
                >
                  {t('auth.forgotPassword.backToSignIn')}
                </Link>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};